import type { FC, ReactNode } from 'react'
import type { ListingSummary } from '@/types'

import { useEffect, useMemo, useState } from 'react'
import {
    ChevronLeft,
    ChevronRight,
    Grid2x2,
    X
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { listingPhotos } from '@/components/homes/listing-card'
import { Photo } from '@/components/generals/photo'
import { SaveButton } from '@/components/homes/save-button'
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog'

interface PhotoTourDialogProps {
    listing: ListingSummary
    open: boolean
    onOpenChange: (open: boolean) => void
    startIndex: number | null
}

type TourPhoto = ReturnType<typeof listingPhotos>[number] & { index: number }

/*
 * Full-screen photo tour: photos grouped by room, and a one-at-a-time viewer
 * that opens on whichever photo was clicked.
 */
export const PhotoTourDialog: FC<PhotoTourDialogProps> = ({ listing, open, onOpenChange, startIndex }): ReactNode => {
    const photos: TourPhoto[] = useMemo(() => listingPhotos(listing).map((photo, index) => ({ ...photo, index })), [listing])
    const [viewer, setViewer] = useState<number | null>(null)

    useEffect(() => {
        if (open) setViewer(startIndex)
    }, [open, startIndex])

    useEffect(() => {
        if (!open || viewer === null) return

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'ArrowRight') setViewer(current => (current === null ? 0 : (current + 1) % photos.length))
            if (event.key === 'ArrowLeft') setViewer(current => (current === null ? 0 : (current - 1 + photos.length) % photos.length))
        }

        window.addEventListener('keydown', onKeyDown)

        return () => window.removeEventListener('keydown', onKeyDown)
    }, [open, viewer, photos.length])

    const groups = useMemo(() => {
        const map = new Map<string, { room: string, photoId: string | null, photos: TourPhoto[] }>()

        for (const photo of photos) {
            const room = photo.room?.trim() || 'More photos'
            const group = map.get(room) ?? { room, photoId: photo.id === 'cover' ? null : photo.id, photos: [] }
            group.photos.push(photo)
            map.set(room, group)
        }

        return Array.from(map.values())
    }, [photos])

    const current = viewer !== null ? photos[viewer] : null

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                showCloseButton={false}
                className={cn(
                    'top-0 left-0 flex h-dvh max-h-dvh w-full max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none p-0 sm:max-w-none',
                    current ? 'bg-black text-white' : 'bg-white'
                )}
            >
                <DialogTitle className='sr-only'>Photo tour of {listing.title}</DialogTitle>
                <DialogDescription className='sr-only'>Browse every photo of this home, grouped by room. Use the arrow keys to move between photos.</DialogDescription>

                <div className='flex h-16 shrink-0 items-center justify-between px-4 sm:px-6'>
                    {current
                        ? (
                            <button
                                type='button'
                                onClick={() => setViewer(null)}
                                className='flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold hover:bg-white/10'
                            >
                                <Grid2x2 className='size-4' aria-hidden='true' />
                                All photos
                            </button>
                        )
                        : (
                            <button
                                type='button'
                                onClick={() => onOpenChange(false)}
                                className='flex size-10 items-center justify-center rounded-full hover:bg-muted'
                                aria-label='Close photo tour'
                            >
                                <ChevronLeft className='size-5' aria-hidden='true' />
                            </button>
                        )}

                    {current && (
                        <p className='text-sm font-medium tabular-nums'>
                            <span>{(viewer ?? 0) + 1}</span> / <span>{photos.length}</span>
                        </p>
                    )}

                    <div className='flex items-center gap-1'>
                        {!current && <SaveButton listingId={listing.id} title={listing.title} variant='inline' />}
                        {current && (
                            <button
                                type='button'
                                onClick={() => onOpenChange(false)}
                                className='flex size-10 items-center justify-center rounded-full hover:bg-white/10'
                                aria-label='Close photo tour'
                            >
                                <X className='size-5' aria-hidden='true' />
                            </button>
                        )}
                    </div>
                </div>

                {current
                    ? (
                        <div className='relative flex min-h-0 flex-1 flex-col items-center justify-center px-4 pb-6 sm:px-20'>
                            <div className='relative h-full max-h-[80dvh] w-full max-w-6xl'>
                                <Photo src={current.image} alt={current.image_alt} sizes='100vw' className='object-contain' priority />
                            </div>

                            {current.room && (
                                <p className='mt-4 text-sm text-white/80'>
                                    {current.id === 'cover'
                                        ? current.room
                                        : <span data-mdlfy-cms={`listing_photos:${current.id}:room`}>{current.room}</span>}
                                </p>
                            )}

                            {photos.length > 1 && (
                                <>
                                    <button
                                        type='button'
                                        onClick={() => setViewer(index => ((index ?? 0) - 1 + photos.length) % photos.length)}
                                        className='absolute top-1/2 left-3 flex size-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/40 bg-black/40 transition hover:bg-black/70 sm:left-6'
                                        aria-label='Previous photo'
                                    >
                                        <ChevronLeft className='size-5' aria-hidden='true' />
                                    </button>
                                    <button
                                        type='button'
                                        onClick={() => setViewer(index => ((index ?? 0) + 1) % photos.length)}
                                        className='absolute top-1/2 right-3 flex size-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/40 bg-black/40 transition hover:bg-black/70 sm:right-6'
                                        aria-label='Next photo'
                                    >
                                        <ChevronRight className='size-5' aria-hidden='true' />
                                    </button>
                                </>
                            )}
                        </div>
                    )
                    : (
                        <div className='flex-1 overflow-y-auto px-4 pb-16 sm:px-6'>
                            <div className='mx-auto max-w-4xl'>
                                <h2 className='font-heading text-2xl font-semibold'>Photo tour</h2>

                                <ul className='mt-6 grid grid-cols-3 gap-4 sm:grid-cols-5'>
                                    {groups.map(group => (
                                        <li key={group.room}>
                                            <button
                                                type='button'
                                                onClick={() => document.getElementById(`tour-${group.photos[0].index}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                                                className='group block w-full text-left'
                                            >
                                                <span className='relative block aspect-square overflow-hidden rounded-lg bg-muted'>
                                                    <Photo src={group.photos[0].image} alt={group.photos[0].image_alt} sizes='150px' />
                                                </span>
                                                <span className='mt-1.5 block truncate text-sm font-medium group-hover:underline'>
                                                    {group.photoId
                                                        ? <span data-mdlfy-cms={`listing_photos:${group.photoId}:room`}>{group.room}</span>
                                                        : group.room}
                                                </span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>

                                <div className='mt-12 space-y-14'>
                                    {groups.map(group => (
                                        <section key={group.room} aria-label={group.room} className='grid gap-6 md:grid-cols-[12rem_1fr]'>
                                            <h3 className='text-xl font-semibold'>
                                                {group.photoId
                                                    ? <span data-mdlfy-cms={`listing_photos:${group.photoId}:room`}>{group.room}</span>
                                                    : group.room}
                                            </h3>

                                            <div className='grid grid-cols-2 gap-2'>
                                                {group.photos.map((photo, index) => (
                                                    <button
                                                        key={photo.id}
                                                        id={`tour-${photo.index}`}
                                                        type='button'
                                                        onClick={() => setViewer(photo.index)}
                                                        className={cn(
                                                            'relative block scroll-mt-4 overflow-hidden rounded-lg bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
                                                            index % 3 === 0 ? 'col-span-2 aspect-[3/2]' : 'aspect-square'
                                                        )}
                                                        aria-label={`Open ${photo.image_alt || 'photo'}`}
                                                    >
                                                        <Photo src={photo.image} alt={photo.image_alt} sizes='(min-width: 768px) 60vw, 100vw' className='transition hover:brightness-90' />
                                                    </button>
                                                ))}
                                            </div>
                                        </section>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
            </DialogContent>
        </Dialog>
    )
}
